import React from 'react';
import { useSelector } from 'react-redux';
import classNames from 'classnames';
import { Link } from 'react-router-dom';
import CopyToClipboard, { useCopyToClipboard } from '../../components/CopyToClipboard';
import Input from '../../components/form/FormikInput';
import FormattedPlural from '../../components/FormattedPlural';
import Loading from '../../components/Loading';
import StatusButton from '../../components/StatusButton';
import { useCreateGiveawayCodeMutation, useGetGiveawayCodesByTitleIdQuery } from '../../services/titles';
import { useQueryStringParam } from '../../utils/routing';
import ErrorPage404 from '../404';

const PER_PAGE = 10;

const GiveawayCodes = (props) => {
    const session = useSelector((state) => state.session);
    const titleId = props.match.params.catId;
    const offset = Number(useQueryStringParam('offset') || 0);
    const [copyText, { isSuccess: copySuccess, error: copyAllError }] = useCopyToClipboard({
        noTextMessage: 'No Codes to copy. Generate at least one code to copy.'
    });
    const { data, isFetching, isError, refetch } = useGetGiveawayCodesByTitleIdQuery(
        { titleId, limit: PER_PAGE, offset },
        {
            skip: !session?.isAdmin,
            refetchOnMountOrArgChange: true
        }
    );
    const [createCode, { isLoading: isCreating }] = useCreateGiveawayCodeMutation();

    const [codeCount, setCodeCount] = React.useState(1);

    const handleCreateCodes = async () => {
        await createCode({ titleId, codeCount: Number(codeCount) });
        setCodeCount(1);
    };

    const handleCopyPageCodes = () => {
        const codes = data.results.map((r) => r.code).join('\n');
        copyText(codes);
    };

    if (!session.isAdmin) return <ErrorPage404 />;

    const page = Math.round(offset / PER_PAGE) + 1;
    const pageCount = Math.round((data?.count || 0) / PER_PAGE);

    const hasPrevPage = page !== 1;
    const hasNextPage = page !== pageCount;

    const prevPage = hasPrevPage ? offset - PER_PAGE : offset;
    const nextPage = hasNextPage ? offset + PER_PAGE : offset;

    return (
        <>
            <div className="row">
                <div className="col-xs-12 col-md-4">
                    <h1>Giveaway Codes</h1>
                </div>
            </div>
            {isError && (
                <div>
                    <p>Unable to load giveaway codes.</p>
                    <button className="btn btn-sm btn-primary" onClick={() => refetch()}>
                        Try again
                    </button>
                </div>
            )}
            {isFetching && <Loading />}
            {data && !isError && !isFetching && (
                <>
                    <div className="row middle">
                        <div className="col-xs-12 col-md-2">
                            <Input
                                id="code_count"
                                label="Generate Codes"
                                min={1}
                                max={50000}
                                value={codeCount}
                                type="number"
                                pattern="[0-9]+"
                                onChange={(e) => setCodeCount(e.target.value)}
                                append={
                                    <button
                                        className="btn btn-secondary"
                                        type="button"
                                        onClick={handleCreateCodes}
                                        disabled={isCreating}
                                    >
                                        <FormattedPlural value={codeCount} one="Generate Code" other="Generate Codes" />
                                    </button>
                                }
                                hideLabel
                            />
                        </div>
                        <div className="col-xs-12 col-md-10">
                            <StatusButton
                                successState={copySuccess ? 'success' : null}
                                label="Copy all codes on page"
                                successLabel="Copied!"
                                defaultClass="btn-secondary"
                                className="btn"
                                type="button"
                                onClick={handleCopyPageCodes}
                            />
                            {copyAllError && <span className="txt-danger">{copyAllError}</span>}
                        </div>
                    </div>
                    <table className="table">
                        <thead>
                            <tr>
                                <th>Code</th>
                                <th>Created</th>
                                <th>Redeemed</th>
                                <th>Copy</th>
                            </tr>
                        </thead>
                        <tbody>
                            {!data.results.length && (
                                <tr>
                                    <td colSpan="4">No Codes generated for this title</td>
                                </tr>
                            )}
                            {data.results.map(({ code, created_at, redeemed, redeemed_at }) => (
                                <tr key={code}>
                                    <td>{code}</td>
                                    <td>{created_at}</td>
                                    <td>
                                        {!redeemed && 'Not Redeemed'} {redeemed_at}
                                    </td>
                                    <td>
                                        <CopyToClipboard text={code} />
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                    {!!data.results.length && (
                        <div className="sales-pagination mb-5">
                            <Link
                                to={`?offset=${prevPage}`}
                                className={classNames('btn btn-sm m-0', { disabled: !hasPrevPage })}
                                disabled={!hasPrevPage}
                            >
                                Prev
                            </Link>

                            <span>
                                Page {page} of {pageCount}
                            </span>

                            <Link
                                to={`?offset=${nextPage}`}
                                className={classNames('btn btn-sm m-0', { disabled: !hasNextPage })}
                                disabled={!hasNextPage}
                            >
                                Next
                            </Link>
                        </div>
                    )}
                </>
            )}
        </>
    );
};

export default GiveawayCodes;
